import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  collection,
  query,
  orderBy,
  getDoc,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Search,
  Plus,
  X,
  Calendar,
  DollarSign,
  GripVertical,
  LayoutGrid,
  ArrowRight,
  Inbox,
  Rocket,
  CheckCircle2,
  Copy,
  Trash2,
  ExternalLink,
  Globe,
  Share2,
  Target,
  Newspaper,
  Star,
  Plug,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useResponsive } from '../../contexts/ResponsiveContext';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
import BackButton from '../../components/BackButton';
import HiringAvatarCluster from '../../components/hr/HiringAvatarCluster';
import { fetchPortalUsersLinkedToAccounts } from '../../services/accountTeamMembersService';
import { getMarketingFulfillmentData } from '../../services/marketingFulfillmentDataService';
import { isActiveClientAccount } from '../../utils/accountLifecycle'; // one client-lifecycle rule

// Service lines, in the order they appear across the top of the board. The
// first five bucket `marketingRequests` by their stored `category`; Website
// Plugins reads the separate `pluginRequests` collection and is read-only here
// (the plugin detail page owns its status).
const SERVICE_LINES = [
  {
    key: 'website',
    label: 'Website',
    emoji: '🌐',
    Icon: Globe,
    doneLabel: 'Live',
    requestTitle: 'Website',
    requestDescription: (company) => `Website client: ${company}`,
    clientRoute: 'website',
  },
  {
    key: 'social',
    label: 'Social Media',
    emoji: '📱',
    Icon: Share2,
    doneLabel: 'Active',
    requestTitle: 'Social Media Management',
    requestDescription: (company) => `Social media management client: ${company}`,
    clientRoute: 'social',
  },
  {
    key: 'ads',
    label: 'Ads',
    emoji: '🎯',
    Icon: Target,
    doneLabel: 'Active',
    requestTitle: 'Paid Advertising',
    requestDescription: (company) => `Paid advertising client: ${company}`,
    clientRoute: 'ads',
  },
  {
    key: 'newsletter',
    label: 'Newsletter',
    emoji: '📰',
    Icon: Newspaper,
    doneLabel: 'Active',
    requestTitle: 'Email Newsletter',
    requestDescription: (company) => `Newsletter client: ${company}`,
    clientRoute: 'newsletter',
  },
  {
    key: 'reviews',
    label: 'Google My Business',
    emoji: '⭐',
    Icon: Star,
    doneLabel: 'Active',
    requestTitle: 'Google My Business',
    requestDescription: (company) => `Google Business Profile client: ${company}`,
    clientRoute: 'reviews',
  },
  {
    key: 'plugins',
    label: 'Website Plugins',
    emoji: '🔌',
    Icon: Plug,
    doneLabel: 'Active',
    readOnly: true,
  },
];

// Kanban stages, in pipeline order. Every service line shares the same three
// stages; only the last one's title changes (Website goes "Live", the rest go
// "Active"). Plugins have no working stage — they're either requested or
// active — so their board drops the middle column.
const STAGES = [
  {
    key: 'requested',
    title: 'Requested',
    emoji: '📥',
    color: '#F59E0B',
    tile: 'bg-amber-50 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-amber-500',
    empty: 'No new requests — add a client to get started.',
    Icon: Inbox,
  },
  {
    key: 'in-progress',
    title: 'In Progress',
    emoji: '🚀',
    color: '#3B82F6',
    tile: 'bg-blue-50 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-blue-500',
    empty: 'Nothing is being worked right now.',
    Icon: Rocket,
  },
  {
    key: 'done',
    title: 'Active',
    emoji: '🏁',
    color: '#10B981',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-emerald-500',
    empty: 'Nothing is live yet.',
    Icon: CheckCircle2,
  },
];

// Stored request status → stage. Legacy 'pending' folds into Requested and
// 'approved' into In Progress; cancelled/denied are terminal and stay off the
// board.
const STATUS_TO_STAGE = {
  requested: 'requested',
  pending: 'requested',
  'in-progress': 'in-progress',
  approved: 'in-progress',
  active: 'done',
  completed: 'done',
};

// Stage → the status written back on the marketingRequest when a card is
// dropped there. 'active' is what the client portal reads as live/active.
const STAGE_TO_STATUS = {
  requested: 'requested',
  'in-progress': 'in-progress',
  done: 'active',
};

// Plugin requests only know requested vs. active.
const PLUGIN_STATUS_TO_STAGE = {
  requested: 'requested',
  pending: 'requested',
  active: 'done',
  approved: 'done',
  completed: 'done',
};

const stagesFor = (line) => (line.readOnly ? STAGES.filter((s) => s.key !== 'in-progress') : STAGES);

const stageTitle = (stage, line) => (stage.key === 'done' ? line.doneLabel : stage.title);

const displayName = (userData) =>
  userData.displayName ||
  (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
  userData.email ||
  'Unknown User';

const toIso = (value) => {
  if (!value) return new Date().toISOString();
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  return value;
};

const MarketingFulfillment = () => {
  const { darkMode } = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeLineKey, setActiveLineKey] = useState(location.state?.activeTab || 'website');
  const [requests, setRequests] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Client accounts + linked portal users (drives the card avatars/names and
  // the Add Client picker).
  const [clients, setClients] = useState([]);
  const [teamByAccountId, setTeamByAccountId] = useState({});
  const [loadingClients, setLoadingClients] = useState(true);

  // Drag-and-drop status change
  const [draggingRequestId, setDraggingRequestId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Search + stage filter state ('' = full board)
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  // Add an existing client to the active service line
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addingClientId, setAddingClientId] = useState(null);

  // Detail modal for requests that aren't linked to a client account (legacy
  // free-form requests). Account-linked cards open the client's own page.
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [fulfillmentData, setFulfillmentData] = useState(null);
  const [fulfillmentLoading, setFulfillmentLoading] = useState(false);

  const activeLine = SERVICE_LINES.find((l) => l.key === activeLineKey) || SERVICE_LINES[0];
  const isPlugins = activeLine.readOnly === true;

  // Switching service lines resets the focused stage so the board is shown.
  const selectLine = (key) => {
    setActiveLineKey(key);
    setStageFilter('');
  };

  // Load client accounts once, plus the portal users linked to each.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingClients(true);
      try {
        const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.company)
          .sort((a, b) => String(a.company).localeCompare(String(b.company)));
        setClients(list);

        fetchPortalUsersLinkedToAccounts(list)
          .then((teamMap) => {
            if (cancelled) return;
            const next = {};
            teamMap.forEach((members, id) => { next[id] = members || []; });
            setTeamByAccountId(next);
          })
          .catch((err) => console.error('Error loading account team members:', err));
      } catch (error) {
        console.error('Error loading client accounts:', error);
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live marketing requests. Requests submitted from the portal may only carry
  // a userId — resolve the requester's name/company/account from their user doc.
  useEffect(() => {
    const q = query(collection(db, 'marketingRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      async (snap) => {
        const fetched = await Promise.all(
          snap.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            let requestedByName = data.requestedByName || 'Unknown User';
            let companyName = data.companyName || 'Unknown Company';
            let accountId = data.accountId || null;

            if ((requestedByName === 'Unknown User' || companyName === 'Unknown Company' || !accountId) && (data.userId || data.userEmail)) {
              try {
                let userData = null;
                if (data.userId) {
                  const userDoc = await getDoc(doc(db, 'users', data.userId));
                  if (userDoc.exists()) userData = userDoc.data();
                }
                if (!userData && data.userEmail) {
                  const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', data.userEmail)));
                  if (!usersSnap.empty) userData = usersSnap.docs[0].data();
                }
                if (userData) {
                  if (requestedByName === 'Unknown User') requestedByName = displayName(userData);
                  if (companyName === 'Unknown Company') companyName = userData.company || 'Unknown Company';
                  if (!accountId) accountId = userData.accountId || null;
                }
              } catch (error) {
                console.error('Error fetching user data:', error);
              }
            }

            return {
              id: docSnapshot.id,
              ...data,
              accountId,
              status: data.status || 'pending',
              category: data.category || 'website',
              priority: data.priority || 'normal',
              requestDate: toIso(data.createdAt),
              requestedByName,
              companyName,
            };
          })
        );
        setRequests(fetched);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching marketing requests:', error);
        setRequests([]);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Website plugin requests (separate collection).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'pluginRequests'), orderBy('requestedAt', 'desc')));
        const fetched = await Promise.all(
          snap.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            let requestedByName = data.requestedByName || 'Unknown User';
            let companyName = data.companyName || data.company || 'Unknown Company';
            if (data.userId && (requestedByName === 'Unknown User' || companyName === 'Unknown Company')) {
              try {
                const userDoc = await getDoc(doc(db, 'users', data.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  if (requestedByName === 'Unknown User') requestedByName = displayName(userData);
                  if (companyName === 'Unknown Company') companyName = userData.company || 'Unknown Company';
                }
              } catch (error) {
                console.error('Error fetching user data:', error);
              }
            }
            return {
              id: docSnapshot.id,
              ...data,
              status: data.status || 'requested',
              requestDate: toIso(data.requestedAt),
              requestedByName,
              companyName,
            };
          })
        );
        if (!cancelled) setPlugins(fetched);
      } catch (error) {
        console.error('Error fetching plugins:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Client-provided fulfillment data for the legacy detail modal.
  useEffect(() => {
    if (!selectedRequest) {
      setFulfillmentData(null);
      return undefined;
    }
    const ownerId = selectedRequest.accountId || selectedRequest.userId;
    if (!ownerId) {
      setFulfillmentData(null);
      return undefined;
    }
    let cancelled = false;
    setFulfillmentLoading(true);
    getMarketingFulfillmentData(ownerId)
      .then((data) => { if (!cancelled) setFulfillmentData(data); })
      .catch((err) => {
        console.error('Load client fulfillment data error:', err);
        if (!cancelled) setFulfillmentData(null);
      })
      .finally(() => { if (!cancelled) setFulfillmentLoading(false); });
    return () => { cancelled = true; };
  }, [selectedRequest]);

  // ---- Account resolution ----
  const clientsById = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  const clientsByCompany = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      if (c.company) map[String(c.company).toLowerCase().trim()] = c;
    });
    return map;
  }, [clients]);

  const clientsByEmail = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      const email = (c.contact?.email || c.email || '').toLowerCase().trim();
      if (email) map[email] = c;
    });
    return map;
  }, [clients]);

  const resolveAccount = (request) => {
    if (request.accountId && clientsById[request.accountId]) return clientsById[request.accountId];
    const name = (request.companyName || request.company || '').toLowerCase().trim();
    if (name && clientsByCompany[name]) return clientsByCompany[name];
    const email = (request.userEmail || '').toLowerCase().trim();
    return email ? clientsByEmail[email] || null : null;
  };

  // Full team-member names for an account, else CRM primary contact, else the
  // name stored on the request.
  const namesFor = (account, request) => {
    const members = account ? (teamByAccountId[account.id] || []) : [];
    if (members.length) return members.map((m) => m.name).filter(Boolean).join(', ');
    const contact = account ? `${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim() : '';
    if (contact) return contact;
    return request?.requestedByName && request.requestedByName !== 'Unknown User' ? request.requestedByName : '';
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatShortDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // ---- Search + bucketing ----
  const searchLower = search.toLowerCase().trim();
  const matchesSearch = (item, account) => {
    if (!searchLower) return true;
    const haystack = [
      item.companyName,
      item.company,
      item.requestedByName,
      item.userEmail,
      item.title,
      item.description,
      item.websiteUrl,
      item.pluginName,
      Array.isArray(item.platforms) ? item.platforms.join(' ') : '',
      namesFor(account, item),
    ];
    return haystack.some((v) => String(v || '').toLowerCase().includes(searchLower));
  };

  // Every service line's counts (for the selector badges) and the active
  // line's cards bucketed by stage.
  const countByLine = {};
  SERVICE_LINES.forEach((l) => { countByLine[l.key] = 0; });
  requests.forEach((r) => {
    if (countByLine[r.category] !== undefined && STATUS_TO_STAGE[r.status]) countByLine[r.category] += 1;
  });
  countByLine.plugins = plugins.filter((p) => PLUGIN_STATUS_TO_STAGE[p.status]).length;

  const cardsByStage = { requested: [], 'in-progress': [], done: [] };
  if (isPlugins) {
    plugins.forEach((plugin) => {
      const stage = PLUGIN_STATUS_TO_STAGE[plugin.status];
      if (!stage) return;
      const account = resolveAccount(plugin);
      if (!matchesSearch(plugin, account)) return;
      cardsByStage[stage].push({ item: plugin, account });
    });
  } else {
    requests.forEach((request) => {
      if (request.category !== activeLine.key) return;
      const stage = STATUS_TO_STAGE[request.status];
      if (!stage) return;
      const account = resolveAccount(request);
      if (!matchesSearch(request, account)) return;
      cardsByStage[stage].push({ item: request, account });
    });
  }

  const cardsFor = (stageKey) => cardsByStage[stageKey] || [];

  // ---- Status changes (drag-and-drop) ----
  const updateRequestStatus = async (requestId, stageKey) => {
    const newStatus = STAGE_TO_STATUS[stageKey];
    if (!newStatus) return;
    const request = requests.find((r) => r.id === requestId);
    if (!request || request.status === newStatus) return;

    // Optimistic update so the card jumps columns immediately.
    setRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r)));
    try {
      await updateDoc(doc(db, 'marketingRequests', requestId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error updating request status:', error);
      setRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, status: request.status } : r)));
      alert('Could not update status. Please try again.');
    }
  };

  const handleStageDrop = (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    const requestId = e.dataTransfer.getData('text/plain') || draggingRequestId;
    setDraggingRequestId(null);
    if (requestId && !isPlugins) updateRequestStatus(requestId, stageKey);
  };

  // ---- Add an existing client to the active service line ----
  const isActiveClient = (account) => isActiveClientAccount(account);

  const lineHasClient = (account) =>
    requests.some((r) => r.category === activeLine.key && (r.accountId === account.id ||
      (r.companyName && r.companyName === account.company)));

  const addSearchLower = addSearch.toLowerCase().trim();
  const pickerClients = clients.filter((account) => {
    if (!isActiveClient(account)) return false;
    if (!addSearchLower) return true;
    return String(account.company || '').toLowerCase().includes(addSearchLower) ||
      namesFor(account).toLowerCase().includes(addSearchLower);
  });

  const handleAddClient = async (account) => {
    if (isPlugins || lineHasClient(account)) return;
    setAddingClientId(account.id);
    try {
      const contact = account.contact || {};
      const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      await addDoc(collection(db, 'marketingRequests'), {
        category: activeLine.key,
        status: 'requested',
        companyName: account.company || '',
        requestedByName: contactName || 'Unknown User',
        accountId: account.id,
        userId: null,
        userEmail: contact.email || null,
        title: activeLine.requestTitle,
        description: activeLine.requestDescription(account.company || ''),
        priority: 'normal',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setShowAddModal(false);
      setAddSearch('');
    } catch (err) {
      console.error('Error adding client:', err);
      alert('Failed to add client. Please try again.');
    } finally {
      setAddingClientId(null);
    }
  };

  // ---- Legacy detail modal ----
  const openCard = (entry) => {
    const { item, account } = entry;
    if (isPlugins) {
      navigate(`/crm/fulfillment/plugin/${item.id}`, { state: { fromMarketingFulfillment: true } });
      return;
    }
    const accountId = account?.id || item.accountId;
    if (accountId) {
      navigate(`/crm/fulfillment/marketing/${activeLine.clientRoute}/${accountId}`);
    } else {
      setSelectedRequest(item);
    }
  };

  const handleModalStatusChange = async (newStatus) => {
    if (!selectedRequest || newStatus === selectedRequest.status) return;
    setSavingStatus(true);
    try {
      await updateDoc(doc(db, 'marketingRequests', selectedRequest.id), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      setRequests((prev) => prev.map((r) => (r.id === selectedRequest.id ? { ...r, status: newStatus } : r)));
      setSelectedRequest((prev) => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error saving status:', error);
      alert('Could not update status. Please try again.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;
    if (!window.confirm('Are you sure you want to delete this request? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'marketingRequests', selectedRequest.id));
      setRequests((prev) => prev.filter((r) => r.id !== selectedRequest.id));
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Could not delete request. Please try again.');
    }
  };

  const copyText = (value, e) => {
    e?.stopPropagation();
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(value).catch(() => {});
  };

  // ---- Shared style tokens ----
  const surface = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const cardBase = `${surface} rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer`;
  const inputBase = `w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  }`;
  const labelBase = 'block text-xs font-semibold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400';
  const chip = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';

  // The one line of detail a card shows under the company, per service line.
  const detailFor = (item) => {
    if (isPlugins) return item.pluginName || 'Untitled plugin';
    switch (activeLine.key) {
      case 'website':
        return item.websiteUrl || '';
      case 'social':
      case 'ads':
        return Array.isArray(item.platforms) && item.platforms.length ? item.platforms.join(' · ') : '';
      case 'newsletter':
        return item.totalSubscribers ? `${item.totalSubscribers} subscribers` : '';
      default:
        return '';
    }
  };

  // Extra chips beside the date, per service line.
  const extraChips = (item) => {
    const chips = [];
    if (activeLine.key === 'ads') {
      if (item.adsPerMonth) chips.push(<span key="ads" className={chip}>{item.adsPerMonth} ads/mo</span>);
      if (item.adBudgetPerMonth) {
        chips.push(
          <span key="budget" className={chip}>
            <DollarSign className="w-3 h-3" />{String(item.adBudgetPerMonth).replace(/^\$/, '')}/mo
          </span>
        );
      }
    }
    if (activeLine.key === 'newsletter' && item.newslettersPerMonth) {
      chips.push(<span key="nl" className={chip}>{item.newslettersPerMonth}/mo</span>);
    }
    if (!isPlugins && activeLine.key !== 'ads' && item.budget) {
      chips.push(
        <span key="budget" className={chip}>
          <DollarSign className="w-3 h-3" />{String(item.budget).replace(/^\$/, '')}
        </span>
      );
    }
    return chips;
  };

  // Card — draggable between stage columns (except plugins); opens the client's
  // page on click, or the detail modal for requests with no linked account.
  const renderCard = (entry) => {
    const { item, account } = entry;
    const names = namesFor(account, item);
    const stage = STAGES.find((s) => s.key === (isPlugins ? PLUGIN_STATUS_TO_STAGE[item.status] : STATUS_TO_STAGE[item.status]));
    const detail = detailFor(item);
    const draggable = !isPlugins;
    return (
      <div
        key={item.id}
        draggable={draggable}
        onDragStart={draggable ? (e) => {
          e.dataTransfer.setData('text/plain', item.id);
          e.dataTransfer.effectAllowed = 'move';
          setDraggingRequestId(item.id);
        } : undefined}
        onDragEnd={draggable ? () => {
          setDraggingRequestId(null);
          setDragOverStage(null);
        } : undefined}
        onClick={() => openCard(entry)}
        className={`group relative ${cardBase} p-4 ${draggingRequestId === item.id ? 'opacity-50 ring-2 ring-dashed ring-gray-300' : ''}`}
      >
        {draggable && (
          <GripVertical className="absolute top-3 right-2.5 w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
        )}
        <div className="flex flex-col items-center text-center">
          <HiringAvatarCluster
            account={account}
            company={item.companyName}
            teamMembers={account ? (teamByAccountId[account.id] || []) : []}
            showNames={false}
          />
          <h4 className="w-full font-semibold text-sm text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">
            {item.companyName}
          </h4>
          {names && (
            <p className="w-full text-xs text-gray-500 dark:text-gray-400 truncate">{names}</p>
          )}
          {detail && (
            <p
              className="mt-2 w-full text-sm font-medium break-words [overflow-wrap:anywhere]"
              style={{ color: stage?.color }}
            >
              {detail}
            </p>
          )}
          {isPlugins && item.requestApiKey && (
            <button
              type="button"
              onClick={(e) => copyText(item.requestApiKey, e)}
              className="mt-2 inline-flex items-center gap-1 max-w-full px-2 py-1 rounded-lg text-[11px] font-mono bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
              title="Copy API key"
            >
              <span className="truncate">{item.requestApiKey}</span>
              <Copy className="w-3 h-3 shrink-0" />
            </button>
          )}
          <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
            {extraChips(item)}
            {formatShortDate(item.requestDate) && (
              <span className={chip}>
                <Calendar className="w-3 h-3" />
                {formatShortDate(item.requestDate)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Table view — shown when a single stage tile is selected.
  const renderTable = () => {
    const stage = STAGES.find((s) => s.key === stageFilter);
    const rows = cardsFor(stageFilter);
    const thBase = 'px-5 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider';
    const tdBase = 'px-5 py-3.5 text-sm text-gray-900 dark:text-white';
    const rowClass = 'hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors';
    const detailHeader = isPlugins ? 'Plugin'
      : activeLine.key === 'website' ? 'Website'
        : activeLine.key === 'newsletter' ? 'Subscribers'
          : activeLine.key === 'reviews' ? 'Service'
            : 'Platforms';

    return (
      <div className={`${surface} rounded-2xl shadow-sm overflow-hidden`}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage?.color }} />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{stage ? stageTitle(stage, activeLine) : ''}</h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {rows.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setStageFilter('')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Back to board
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                <th className={thBase}>Company</th>
                <th className={thBase}>Team</th>
                <th className={thBase}>{detailHeader}</th>
                {isPlugins && <th className={thBase}>API key</th>}
                {activeLine.key === 'ads' && <th className={thBase}>Ads / Budget</th>}
                {activeLine.key === 'newsletter' && <th className={thBase}>Per Month</th>}
                <th className={thBase}>Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((entry) => {
                const { item, account } = entry;
                const detail = isPlugins
                  ? (item.pluginName || 'Untitled plugin')
                  : activeLine.key === 'reviews' ? (item.title || 'Google My Business') : detailFor(item);
                return (
                  <tr key={item.id} onClick={() => openCard(entry)} className={rowClass}>
                    <td className={tdBase}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 scale-[0.8] origin-left -mr-2">
                          <HiringAvatarCluster account={account} company={item.companyName} teamMembers={[]} showNames={false} />
                        </div>
                        <span className="font-semibold truncate">{item.companyName}</span>
                      </div>
                    </td>
                    <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{namesFor(account, item) || '—'}</td>
                    <td className={`${tdBase} font-medium`}>{detail || '—'}</td>
                    {isPlugins && (
                      <td className={`${tdBase} text-gray-500 dark:text-gray-400`} onClick={(e) => e.stopPropagation()}>
                        {item.requestApiKey ? (
                          <button
                            type="button"
                            onClick={(e) => copyText(item.requestApiKey, e)}
                            className="inline-flex items-center gap-1.5 max-w-[16rem] font-mono text-xs hover:text-gray-900 dark:hover:text-white"
                            title="Copy API key"
                          >
                            <span className="truncate">{item.requestApiKey}</span>
                            <Copy className="w-3.5 h-3.5 shrink-0" />
                          </button>
                        ) : '—'}
                      </td>
                    )}
                    {activeLine.key === 'ads' && (
                      <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>
                        {[item.adsPerMonth && `${item.adsPerMonth} ads/mo`, item.adBudgetPerMonth && `$${String(item.adBudgetPerMonth).replace(/^\$/, '')}/mo`].filter(Boolean).join(' · ') || '—'}
                      </td>
                    )}
                    {activeLine.key === 'newsletter' && (
                      <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{item.newslettersPerMonth || '—'}</td>
                    )}
                    <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{formatDate(item.requestDate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="p-14 text-center">
            {stage && (
              <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center ${stage.tile}`}>
                <stage.Icon className={`w-7 h-7 ${stage.text}`} />
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">{stage?.empty}</p>
          </div>
        )}
      </div>
    );
  };

  // Legacy detail modal helpers.
  const fieldRow = (label, value) => (
    <div>
      <p className={labelBase}>{label}</p>
      <p className="text-sm text-gray-900 dark:text-white break-words">{value || '—'}</p>
    </div>
  );

  const renderFulfillmentData = () => {
    if (fulfillmentLoading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
    if (!fulfillmentData) return <p className="text-sm text-gray-500 dark:text-gray-400">No fulfillment data for this client yet.</p>;
    const categoryKey = selectedRequest.category === 'ads' ? 'paidAds' : selectedRequest.category;
    const cat = fulfillmentData[categoryKey];
    const entries = cat && typeof cat === 'object'
      ? Object.entries(cat).filter(([, v]) => v !== '' && v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : true))
      : [];
    if (entries.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">No fulfillment data for this category yet.</p>;
    const label = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
    return (
      <div className={`rounded-xl p-4 space-y-2 text-sm ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        {entries.map(([key, value]) => (
          <div key={key}>
            <span className="font-medium text-gray-600 dark:text-gray-400">{label(key)}: </span>
            {Array.isArray(value) ? (
              <ul className="list-disc list-inside mt-1">
                {value.map((entry, i) => {
                  const url = typeof entry === 'object' && entry?.url ? entry.url : (typeof entry === 'string' && entry.startsWith('http') ? entry : null);
                  const name = typeof entry === 'object' && entry?.name ? entry.name : (url ? 'Link' : String(entry));
                  return (
                    <li key={i}>
                      {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="text-red-600 dark:text-red-400 hover:underline">{name}</a> : name}
                    </li>
                  );
                })}
              </ul>
            ) : typeof value === 'boolean' ? (
              <span>{value ? 'Yes' : 'No'}</span>
            ) : (
              <span className="text-gray-900 dark:text-white">{String(value)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} />
          <p className={`mt-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading marketing fulfillment…</p>
        </div>
      </div>
    );
  }

  const stages = stagesFor(activeLine);
  const requestedCount = cardsFor('requested').length;
  const inProgressCount = cardsFor('in-progress').length;
  const doneCount = cardsFor('done').length;
  const lineTotal = requestedCount + inProgressCount + doneCount;
  const selectedLineForModal = selectedRequest ? SERVICE_LINES.find((l) => l.key === selectedRequest.category) : null;

  return (
    <div className={`min-h-screen ${isMobile ? 'p-3' : 'p-6'}`}>
      <PortalBackground />
      <div className="relative">
        {/* Header */}
        <div className="relative flex items-center justify-between gap-3 mb-6 min-h-[44px]">
          <BackButton to="/crm/fulfillment" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 dark:text-white whitespace-nowrap`}>
              Marketing Fulfillment
            </h1>
            {!isMobile && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">
                {lineTotal} {activeLine.label.toLowerCase()} client{lineTotal === 1 ? '' : 's'} · {requestedCount} requested · {isPlugins ? `${doneCount} active` : `${inProgressCount} in progress`}
              </p>
            )}
          </div>
          {!isPlugins ? (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold shadow-sm hover:bg-red-700 hover:shadow-md transition-all"
              title={`Add an existing client to ${activeLine.label}`}
            >
              <Plus className="w-4 h-4" /> {isMobile ? 'Add' : 'Add Client'}
            </button>
          ) : (
            <div className="w-10" />
          )}
        </div>

        {/* Service-line selector */}
        <div className="flex justify-center mb-4">
          <div className={`${surface} rounded-2xl shadow-sm p-1.5 inline-flex flex-wrap justify-center gap-1 max-w-full`}>
            {SERVICE_LINES.map((line) => {
              const selected = line.key === activeLine.key;
              const count = countByLine[line.key] || 0;
              return (
                <button
                  key={line.key}
                  type="button"
                  onClick={() => selectLine(line.key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                    selected
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <span aria-hidden="true">{line.emoji}</span>
                  {!isMobile && <span>{line.label}</span>}
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                    selected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stage tiles — click to focus one stage as a table, click again to return to the board */}
        <div className={`grid gap-3 mb-4 ${isMobile ? 'grid-cols-2' : stages.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {stages.map((stage) => {
            const count = cardsFor(stage.key).length;
            const selected = stageFilter === stage.key;
            return (
              <button
                key={stage.key}
                type="button"
                onClick={() => setStageFilter(selected ? '' : stage.key)}
                className={`group rounded-xl border text-left p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  selected
                    ? `border-transparent ring-2 ${stage.ring} ${stage.tile} shadow-sm`
                    : surface
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${stage.tile}`}>
                    <span className="text-2xl" aria-hidden="true">{stage.emoji}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-2xl font-bold leading-none ${count === 0 ? 'text-gray-300 dark:text-gray-600' : stage.text}`}>
                      {count}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{stageTitle(stage, activeLine)}</p>
                  </div>
                  <ArrowRight className={`w-4 h-4 shrink-0 transition-all ${selected ? stage.text : 'text-gray-300 dark:text-gray-600 group-hover:text-gray-500'}`} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder={`Search ${activeLine.label.toLowerCase()} by company, contact, or details…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputBase} pl-10 pr-10 py-3 rounded-xl shadow-sm`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Focused stage → table. Otherwise the full board. */}
        {stageFilter ? renderTable() : (
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : stages.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {stages.map((stage) => {
              const isDropTarget = !isPlugins;
              const cards = cardsFor(stage.key);
              const loadingCol = loadingClients && cards.length === 0 && clients.length === 0;
              const isDragOver = dragOverStage === stage.key;
              const dragging = !!draggingRequestId;
              return (
                <div
                  key={stage.key}
                  onDragOver={isDropTarget ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverStage !== stage.key) setDragOverStage(stage.key); } : undefined}
                  onDragLeave={isDropTarget ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStage((c) => (c === stage.key ? null : c)); } : undefined}
                  onDrop={isDropTarget ? (e) => handleStageDrop(e, stage.key) : undefined}
                  className={`rounded-2xl border flex flex-col transition-all duration-200 ${
                    isDragOver
                      ? `border-transparent ring-2 ${stage.ring} ${stage.tile}`
                      : dragging && isDropTarget
                        ? 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-900/40'
                        : 'border-gray-200/80 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40'
                  }`}
                  style={{ minHeight: isMobile ? '260px' : 'calc(100vh - 400px)' }}
                >
                  {/* Column header */}
                  <div className="flex items-center justify-between gap-2 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{stageTitle(stage, activeLine)}</span>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div
                    className={`grid gap-3 px-3 pb-3 overflow-y-auto flex-1 content-start ${isMobile ? 'grid-cols-1' : 'grid-cols-1 2xl:grid-cols-2'}`}
                    style={{ maxHeight: isMobile ? '420px' : 'calc(100vh - 440px)' }}
                  >
                    {loadingCol ? (
                      <div className="col-span-full flex justify-center items-center py-10">
                        <BBALoader size={32} />
                      </div>
                    ) : cards.length === 0 ? (
                      <div className="col-span-full flex flex-col items-center justify-center text-center py-12 px-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${stage.tile}`}>
                          <stage.Icon className={`w-6 h-6 ${stage.text}`} />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{stage.empty}</p>
                      </div>
                    ) : (
                      cards.map((entry) => renderCard(entry))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Client Modal — picks an existing client into the active service line */}
      {showAddModal && !isPlugins && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'} rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col`}>
            <div className="relative flex items-center justify-center px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Client to {activeLine.label}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">They'll start in Requested</p>
              </div>
              <button
                onClick={() => { setShowAddModal(false); setAddSearch(''); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-5 pb-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Search clients…"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  className={`${inputBase} pl-10`}
                />
              </div>
            </div>

            <div className="px-6 pb-6 overflow-y-auto flex-1 space-y-2">
              {loadingClients ? (
                <div className="flex justify-center py-10"><BBALoader size={32} /></div>
              ) : pickerClients.length === 0 ? (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-10">No clients match.</p>
              ) : (
                pickerClients.map((account) => {
                  const added = lineHasClient(account);
                  const busy = addingClientId === account.id;
                  return (
                    <button
                      key={account.id}
                      type="button"
                      disabled={added || busy}
                      onClick={() => handleAddClient(account)}
                      className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                        added
                          ? 'border-gray-200 dark:border-gray-800 opacity-60 cursor-default'
                          : `${surface} hover:shadow-md hover:-translate-y-0.5`
                      }`}
                    >
                      <div className="shrink-0 scale-[0.8] origin-left -mr-2">
                        <HiringAvatarCluster account={account} company={account.company} teamMembers={[]} showNames={false} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{account.company}</p>
                        {namesFor(account) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{namesFor(account)}</p>
                        )}
                      </div>
                      <span className={`text-xs font-semibold shrink-0 ${added ? 'text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                        {busy ? 'Adding…' : added ? 'Added' : 'Add'}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail modal — only for requests with no linked client account */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'} rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <div className="relative flex items-center justify-center px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedLineForModal?.emoji} {selectedRequest.title || selectedLineForModal?.requestTitle || 'Marketing Request'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{selectedRequest.companyName}</p>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fieldRow('Requested By', selectedRequest.requestedByName)}
                {fieldRow('Email', selectedRequest.userEmail)}
                {fieldRow('Requested', formatDate(selectedRequest.requestDate))}
                {fieldRow('Priority', selectedRequest.priority ? selectedRequest.priority.charAt(0).toUpperCase() + selectedRequest.priority.slice(1) : '')}
                {selectedRequest.category !== 'ads' && fieldRow('Budget', selectedRequest.budget)}
                {selectedRequest.category === 'website' && selectedRequest.websiteUrl && (
                  <div>
                    <p className={labelBase}>Website</p>
                    <a
                      href={selectedRequest.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:underline break-all"
                    >
                      {selectedRequest.websiteUrl} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                    </a>
                  </div>
                )}
                {(selectedRequest.category === 'social' || selectedRequest.category === 'ads') &&
                  fieldRow('Platforms', Array.isArray(selectedRequest.platforms) ? selectedRequest.platforms.join(', ') : '')}
                {selectedRequest.category === 'ads' && fieldRow('Ads / Month', selectedRequest.adsPerMonth)}
                {selectedRequest.category === 'ads' && fieldRow('Budget / Month', selectedRequest.adBudgetPerMonth)}
                {selectedRequest.category === 'newsletter' && fieldRow('Subscribers', selectedRequest.totalSubscribers)}
                {selectedRequest.category === 'newsletter' && fieldRow('Newsletters / Month', selectedRequest.newslettersPerMonth)}
              </div>

              {selectedRequest.description && (
                <div>
                  <p className={labelBase}>Description</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>
              )}

              <div>
                <label className={labelBase}>Status</label>
                <select
                  value={STATUS_TO_STAGE[selectedRequest.status] ? STAGE_TO_STATUS[STATUS_TO_STAGE[selectedRequest.status]] : selectedRequest.status}
                  onChange={(e) => handleModalStatusChange(e.target.value)}
                  disabled={savingStatus}
                  className={inputBase}
                >
                  <option value="requested">Requested</option>
                  <option value="in-progress">In Progress</option>
                  <option value="active">{selectedLineForModal?.doneLabel || 'Active'}</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="denied">Denied</option>
                </select>
                <p className="text-xs mt-1.5 text-gray-500 dark:text-gray-400">
                  Cancelled and denied requests come off the board.
                </p>
              </div>

              <div>
                <p className={labelBase}>Client-Provided Fulfillment Data</p>
                {renderFulfillmentData()}
              </div>
            </div>

            <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={handleDeleteRequest}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button
                onClick={() => setSelectedRequest(null)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${
                  darkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingFulfillment;
